import React, { useState, useEffect } from 'react';
import Widget from 'shared/components/Widget';
import TypographyWithI18N from 'shared/components/TypographyWithI18N';
import { Grid, GridProps, Link } from '@mui/material';
import {
  IRequisition,
  IStageMetaData,
  RequisitionStage,
} from 'modules/hiring/models/myOpenRequisition.model';
import RequisitionTile from './requisitionTile';
import InfiniteScroller from 'shared/components/InfiniteScroller';
import { useTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import IStoreState from 'store/IStoreState';
import {
  getMyOpenRolesData,
  setSelectedOpenRequisition,
} from 'modules/hiring/stateManagement/thunks';
import { useNavigate } from 'react-router-dom';
import { HMRoutingPaths } from 'modules/hiring/models/HMRoutingPaths';
import YourOpenRoleWidgetShimmer from 'modules/hiring/components/yourOpenRoles/yourOpenRoleWidgetShimmer';
import { colors } from 'styles/colors';
import { sortingOptions } from 'modules/hiring/models/sortingOptions';
import SortingToggle from '../sortingToggle';

const GridContainer = (props: GridProps) => (
  <Grid container direction='row' spacing='1.5rem' {...props} />
);

const GridItem = (props: GridProps) => <Grid item sm={12} md={6} {...props} />;

type TYourOpenRolesWidgetStateProps = ReturnType<typeof mapStateToProps>;
type TYourOpenRolesWidgetDispatchProps = {
  getMyOpenRolesData: () => void;
  setSelectedOpenRequisition: (requisition: IRequisition | null) => void;
};

interface IYourOpenRolesWidgetProps
  extends TYourOpenRolesWidgetStateProps,
    TYourOpenRolesWidgetDispatchProps {}

const YourOpenRolesWidget: React.FC<IYourOpenRolesWidgetProps> = ({
  getMyOpenRolesData: getMyOpenRoles,
  setSelectedOpenRequisition: setSelectedRequisition,
  myOpenRoles,
}) => {
  const [reqs, setReqs] = useState<IRequisition[]>();
  const [stagesMeta, setStagesMeta] = useState<IStageMetaData[]>();
  const [visibleTilesCount, setVisibleTilesCount] = useState(6);
  const navigate = useNavigate();
  const { data: yourOpenRolesData, isFetching } = myOpenRoles;

  useEffect(() => {
    getMyOpenRoles();
  }, []);

  useEffect(() => {
    /* istanbul ignore else */
    if (yourOpenRolesData) {
      setReqs(
        yourOpenRolesData?.requisitionList?.sort(
          sortingOptions(translate)[0]?.sortFunction
        )
      );
      setStagesMeta(yourOpenRolesData.metadata?.stages);
    }
  }, [yourOpenRolesData]);

  const contentHeight = `800px`;
  const { t: translate } = useTranslation();
  const onNavigateClick = () => {
    setSelectedRequisition(null);
    navigate('../candidatepipeline');
  };
  const reqHaveCandidate = reqs?.filter(
    (item) => item.currentStage === RequisitionStage.Hiring
  );
  return (
    <Widget
      id='your-open-roles-widget'
      headingProps={{
        i18nKey: 'yourOpenRoles',
        className: 'kfic2-smallHeader',
      }}
      withDivider
      dividerProps={{
        sx: {
          mb: ({ typography }) => typography.pxToRem(20),
          bgcolor: colors.border.gainsboroGrey,
        },
      }}
    >
      {isFetching ? (
        <YourOpenRoleWidgetShimmer />
      ) : reqs && reqs.length > 0 ? (
        <>
          <GridContainer
            id='yor-widget-sortBy-container'
            sx={{ position: 'relative' }}
          >
            {reqHaveCandidate && reqHaveCandidate?.length > 0 && (
              <Link
                data-testid='view-all-Candidates-link'
                className='kfic2-bodyTextSmallLink'
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: ({ typography }) => typography.pxToRem(-30),
                }}
                onClick={onNavigateClick}
              >
                {translate('viewAllCandidates')}
              </Link>
            )}
            <GridItem
              id='yor-widget-sortBy-wrapper'
              data-testid='yor-widget-sortBy-wrapper'
              md={12}
            >
              <SortingToggle
                data-testid='sortingToggle'
                data={reqs}
                setData={setReqs}
                options={sortingOptions(translate)}
                defaultSelectedOption={0}
              />
            </GridItem>
          </GridContainer>
          <InfiniteScroller
            id='yor-widget-infinite-scroller'
            data-testid='yor-widget-infinite-scroller'
            loader={
              <TypographyWithI18N
                lineHeight='4rem'
                align='center'
                i18nKey='loading'
              />
            }
            hasMore={visibleTilesCount < reqs.length}
            loadMore={() =>
              new Promise((resolve) => {
                setTimeout(() => {
                  setVisibleTilesCount(visibleTilesCount + 4);
                  resolve(undefined);
                }, 300);
              })
            }
            sx={{
              height: contentHeight,
              mt: ({ typography }) => typography.pxToRem(20),
            }}
          >
            <GridContainer
              id='yor-tiles-container'
              data-testid='yor-tiles-container'
            >
              {reqs.slice(0, visibleTilesCount).map((req, i) => (
                <GridItem key={req.requisitionID + i} sx={{ width: '100%' }}>
                  <RequisitionTile
                    createdDate={req.dateOpened}
                    curentStep={req.currentStep}
                    currentStage={req.currentStage}
                    id={'req' + i}
                    lastUpdated={req.lastUpdatedDate}
                    owner={req.currentOwner}
                    reqId={req.requisitionID}
                    stagesMeta={stagesMeta as IStageMetaData[]}
                    reqName={req.jobTitle}
                    state={req.state}
                    country={req.country}
                    city={req.city}
                    remoteLocation={req.remoteLocation}
                    onHiringTileClick={() => {
                      setSelectedRequisition(req);
                      navigate(`../${HMRoutingPaths.candidatePipeline}`);
                    }}
                  />
                </GridItem>
              ))}
            </GridContainer>
          </InfiniteScroller>
        </>
      ) : (
        <TypographyWithI18N
          id='your-open-roles-empty-state'
          data-testid='your-open-roles-empty-state'
          i18nKey='noRolesToDisplay'
          align='center'
          lineHeight={contentHeight}
        />
      )}
    </Widget>
  );
};

const mapStateToProps = (state: IStoreState) => ({
  myOpenRoles: state.myOpenRoles,
});

const mapDispatchToProps = {
  getMyOpenRolesData,
  setSelectedOpenRequisition,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(YourOpenRolesWidget);
